import { DomainRuleViolationError } from "./errors.js";
import { DomainValidationError } from "./identifiers.js";
import { validateItineraryItemInput, validateItinerarySectionInput, } from "./itinerary.js";
import { addElapsedMinutes, enteredLocalText, localDateTimeOf, utcOffsetMinutesAt, withTimeEndpoint, zonedWallTimeToInstant, } from "./time-zone.js";
// Targeted edits of an itinerary (CHT-742, contract `docs/architecture/milestone-d-contracts.md` 3.2 and 3.3). Everything here is
// pure: an operation list is planned in memory against a base plan, and the result is the whole new content (sections and items
// with their stable `lineageId`). The application stores it: in place on a DRAFT, or as a revision of the ACTIVE plan. A time is
// always a wall time and a zone; the UTC instant is derived here, and no operation takes an instant from a client.
/** What a change row records (CHT-742): the command that made it. Frozen: the database CHECK is generated from this tuple. */
export const itineraryChangeCommands = Object.freeze([
    "CREATE_PLAN",
    "REPLACE_PLAN",
    "EDIT_DRAFT",
    "PROPOSE_EDIT",
    "APPLY_EDIT",
    "ACTIVATE",
]);
/** Who stood behind a change: nobody beyond the actor, the customer who confirmed a proposal, or staff. */
export const itineraryChangeApprovals = Object.freeze([
    "NONE",
    "CUSTOMER_CONFIRMED",
    "STAFF",
]);
/** Why a validation report row exists. Frozen: the database CHECK is generated from this tuple. */
export const validationReportSources = Object.freeze([
    "EDIT_APPLY",
    "EDIT_PROPOSE",
    "ACTIVATION",
    "VALIDATION_RUN",
]);
/** Bounds of one plan. Item positions are 0 to 999 in the database; a bounded plan leaves room for a two-step renumber. */
export const MAX_ITEMS_PER_ITINERARY = 400;
export const MAX_SECTIONS_PER_ITINERARY = 100;
export const MAX_OPERATIONS_PER_EDIT = 100;
const fail = (field, rule) => new DomainValidationError(field, rule);
const violation = (code) => new DomainRuleViolationError(code);
// --- Helpers --------------------------------------------------------------------------------------------------------------
const present = (value) => value !== null && value !== undefined;
const baseKeyOfSection = (id) => `s:${id}`;
const baseKeyOfItem = (id) => `i:${id}`;
/** One endpoint of a timing as an instant. A gap or an ambiguous time is refused with the endpoint it is about (PR review F6). */
function wallInstantAt(field, wall) {
    try {
        return zonedWallTimeToInstant(wall.date, wall.time, wall.timeZone, wall.disambiguation);
    }
    catch (error) {
        throw withTimeEndpoint(error, field);
    }
}
/** The wall-time fields of a timing: the instants, the zones, the entered text and the offsets. */
function timingFields(timing, context, catalogZone) {
    if (timing.timingKind !== "SCHEDULED") {
        return {
            timingKind: timing.timingKind,
            startsAt: null,
            endsAt: null,
            timeZone: null,
            durationMinutes: null,
            timeZoneSource: null,
            timeZoneObservedAt: null,
            enteredLocalStart: null,
            enteredUtcOffsetMinutes: null,
            endTimeZone: null,
            enteredLocalEnd: null,
            enteredEndUtcOffsetMinutes: null,
        };
    }
    const { start } = timing;
    const end = timing.end ?? null;
    const duration = timing.durationMinutes ?? null;
    if (end !== null && duration !== null) {
        throw fail("durationMinutes", "cannot be given together with an end");
    }
    const from = wallInstantAt("timing.start", start);
    const to = end === null ? null : wallInstantAt("timing.end", end);
    const source = catalogZone === start.timeZone ? "CATALOG" : context.actor;
    return {
        timingKind: "SCHEDULED",
        startsAt: from.instant,
        endsAt: to === null ? null : to.instant,
        timeZone: start.timeZone,
        durationMinutes: duration,
        timeZoneSource: source,
        timeZoneObservedAt: context.now,
        enteredLocalStart: enteredLocalText(start.date, start.time),
        enteredUtcOffsetMinutes: from.utcOffsetMinutes,
        endTimeZone: end === null || end.timeZone === start.timeZone ? null : end.timeZone,
        enteredLocalEnd: end === null ? null : enteredLocalText(end.date, end.time),
        enteredEndUtcOffsetMinutes: to === null ? null : to.utcOffsetMinutes,
    };
}
const catalogZoneOf = (context, catalogItemId) => present(catalogItemId) ? (context.catalogTimeZones?.get(catalogItemId) ?? null) : null;
function assertActorMayWrite(context, fields) {
    if (context.actor === "STAFF")
        return;
    if (fields.staffNote !== undefined)
        throw fail("staffNote", "is not allowed for a customer");
    if (fields.latitude !== undefined || fields.longitude !== undefined) {
        throw fail("latitude", "is entered by staff only");
    }
}
const withoutProvenance = (item) => {
    const { provenance: _provenance, ...data } = item;
    return data;
};
/** An item built from content already judged: the zone, the entered text and the validated record. */
function buildItemUnchecked(content, context) {
    const timing = timingFields(content.timing, context, catalogZoneOf(context, content.catalogItemId));
    return withoutProvenance(validateItineraryItemInput({
        kind: content.kind,
        itemStatus: content.itemStatus ?? null,
        title: content.title,
        ...timing,
        timingKind: timing.timingKind,
        locationName: content.locationName ?? null,
        locality: content.locality ?? null,
        countryCode: content.countryCode ?? null,
        latitude: content.latitude ?? content.ownedPoint?.latitude ?? null,
        longitude: content.longitude ?? content.ownedPoint?.longitude ?? null,
        catalogItemId: content.catalogItemId ?? null,
        externalPlace: content.externalPlace ?? null,
        conciergeRequestId: content.conciergeRequestId ?? null,
        customerNote: content.customerNote ?? null,
        staffNote: content.staffNote ?? null,
        isFixed: content.isFixed ?? false,
        weatherSensitivity: content.weatherSensitivity ?? null,
        provenance: context.provenance,
    }));
}
/** An item built from the content of an ADD_ITEM or a replacement: the actor may only write what the actor may write. */
function buildItem(content, context) {
    assertActorMayWrite(context, content);
    return buildItemUnchecked(content, context);
}
/** The existing item as input again, so a changed copy is validated by the same function that validates a new item. */
function inputOf(data, provenance) {
    return { ...data, provenance };
}
const CHANGEABLE_FIELDS = new Set([
    "title",
    "itemStatus",
    "customerNote",
    "staffNote",
    "locationName",
    "locality",
    "countryCode",
    "latitude",
    "longitude",
    "isFixed",
    "weatherSensitivity",
    "timing",
]);
function applyChanges(data, changes, context) {
    const names = Object.keys(changes).filter((key) => changes[key] !== undefined);
    if (names.length === 0)
        throw fail("changes", "must name a field");
    // The kind, the lineage, the request link and the catalog link never change: a key outside the list is refused, not ignored.
    if (names.some((name) => !CHANGEABLE_FIELDS.has(name))) {
        throw fail("changes", "names a field that cannot change");
    }
    assertActorMayWrite(context, changes);
    if ((changes.latitude === undefined) !== (changes.longitude === undefined)) {
        throw fail("latitude", "needs a longitude and the other way round");
    }
    const timing = changes.timing === undefined
        ? {}
        : timingFields(changes.timing, context, catalogZoneOf(context, data.catalogItemId));
    const { timing: _timing, ...flat } = changes;
    return withoutProvenance(validateItineraryItemInput({
        ...inputOf(data, "REVISION_COPY"),
        ...flat,
        ...timing,
    }));
}
/** A new start for a scheduled item, keeping its elapsed length (an end moves with it; a duration stays). */
function retime(data, start, context) {
    if (data.timingKind !== "SCHEDULED" || data.startsAt === null) {
        throw fail("start", "is only for a scheduled item");
    }
    const from = wallInstantAt("timing.start", start);
    const catalogZone = catalogZoneOf(context, data.catalogItemId);
    let endFields = {};
    if (data.endsAt !== null) {
        const length = data.endsAt.getTime() - data.startsAt.getTime();
        const endsAt = addElapsedMinutes(from.instant, length / 60_000);
        const endZone = data.endTimeZone ?? start.timeZone;
        endFields = {
            endsAt,
            enteredLocalEnd: localDateTimeOf(endsAt, endZone),
            enteredEndUtcOffsetMinutes: utcOffsetMinutesAt(endsAt, endZone),
        };
    }
    return withoutProvenance(validateItineraryItemInput({
        ...inputOf(data, "REVISION_COPY"),
        startsAt: from.instant,
        timeZone: start.timeZone,
        timeZoneSource: catalogZone === start.timeZone ? "CATALOG" : context.actor,
        timeZoneObservedAt: context.now,
        enteredLocalStart: enteredLocalText(start.date, start.time),
        enteredUtcOffsetMinutes: from.utcOffsetMinutes,
        ...endFields,
    }));
}
const sameInstant = (a, b) => a === null || b === null ? a === b : a.getTime() === b.getTime();
/**
 * The names of the groups of fields that differ between two items: `title`, `itemStatus`, `timing`, `location`, `externalPlace`,
 * `customerNote`, `staffNote` and `isFixed`. Used for the change audit (names, never values) and to see what an edit touched.
 */
export function changedFieldNames(a, b) {
    const names = [];
    if (a.title !== b.title)
        names.push("title");
    if (a.itemStatus !== b.itemStatus)
        names.push("itemStatus");
    if (a.timingKind !== b.timingKind ||
        !sameInstant(a.startsAt, b.startsAt) ||
        !sameInstant(a.endsAt, b.endsAt) ||
        a.timeZone !== b.timeZone ||
        a.durationMinutes !== b.durationMinutes ||
        a.endTimeZone !== b.endTimeZone ||
        a.enteredLocalStart !== b.enteredLocalStart ||
        a.enteredUtcOffsetMinutes !== b.enteredUtcOffsetMinutes ||
        a.enteredLocalEnd !== b.enteredLocalEnd ||
        a.enteredEndUtcOffsetMinutes !== b.enteredEndUtcOffsetMinutes ||
        a.timeZoneSource !== b.timeZoneSource) {
        names.push("timing");
    }
    if (a.locationName !== b.locationName ||
        a.locality !== b.locality ||
        a.countryCode !== b.countryCode ||
        a.latitude !== b.latitude ||
        a.longitude !== b.longitude) {
        names.push("location");
    }
    if (a.externalPlace?.provider !== b.externalPlace?.provider ||
        a.externalPlace?.id !== b.externalPlace?.id ||
        a.externalPlace?.name !== b.externalPlace?.name) {
        names.push("externalPlace");
    }
    if (a.customerNote !== b.customerNote)
        names.push("customerNote");
    if (a.staffNote !== b.staffNote)
        names.push("staffNote");
    if (a.isFixed !== b.isFixed)
        names.push("isFixed");
    if (a.weatherSensitivity !== b.weatherSensitivity)
        names.push("weatherSensitivity");
    return names;
}
const bySectionPosition = (a, b) => a.position - b.position;
class Working {
    sections;
    lists = new Map();
    affected = new Set();
    changes = [];
    touched = new Set();
    removed = new Set();
    created = 0;
    constructor(base) {
        this.sections = [];
        if (base === null)
            return;
        for (const section of [...base.sections].sort(bySectionPosition)) {
            const key = baseKeyOfSection(section.id);
            this.sections.push({
                key,
                originSectionId: section.id,
                position: section.position,
                kind: section.kind,
                localDate: section.localDate,
                timeZone: section.timeZone,
                title: section.title,
            });
            this.lists.set(key, []);
        }
        for (const item of [...base.items].sort((a, b) => a.position - b.position || (a.id < b.id ? -1 : 1))) {
            const list = this.lists.get(baseKeyOfSection(item.sectionId));
            if (list === undefined)
                continue;
            list.push({
                key: baseKeyOfItem(item.id),
                lineageId: item.lineageId ?? item.id,
                originItemId: item.id,
                position: item.position,
                data: item.data,
                provenance: "REVISION_COPY",
            });
        }
    }
    get itemCount() {
        let count = 0;
        for (const list of this.lists.values())
            count += list.length;
        return count;
    }
    nextKey() {
        this.created += 1;
        return `n:${this.created}`;
    }
    section(ref) {
        let found;
        if ("sectionId" in ref)
            found = this.sections.find((s) => s.originSectionId === ref.sectionId);
        else if ("localDate" in ref) {
            found = this.sections.find((s) => s.kind === "DAY" && s.localDate === ref.localDate);
        }
        else
            found = this.sections.find((s) => s.kind === "FLEXIBLE");
        if (found === undefined)
            throw violation("itinerary.section_not_found");
        return found;
    }
    find(stableId) {
        for (const [sectionKey, list] of this.lists) {
            const index = list.findIndex((item) => item.lineageId === stableId);
            const item = list[index];
            if (item !== undefined)
                return { sectionKey, index, item };
        }
        throw violation("itinerary.item_not_found");
    }
    sectionOfKey(key) {
        const found = this.sections.find((section) => section.key === key);
        if (found === undefined)
            throw violation("itinerary.section_not_found");
        return found;
    }
}
function addSection(work, input) {
    const section = validateItinerarySectionInput(input);
    if (work.sections.length >= MAX_SECTIONS_PER_ITINERARY) {
        throw fail("section", "the plan has too many sections");
    }
    if (section.kind === "DAY" &&
        work.sections.some((other) => other.kind === "DAY" && other.localDate === section.localDate)) {
        throw fail("section.localDate", "already has a day section");
    }
    if (section.kind === "FLEXIBLE" && work.sections.some((other) => other.kind === "FLEXIBLE")) {
        throw fail("section.kind", "already has an undated section");
    }
    const position = work.sections.reduce((max, other) => Math.max(max, other.position), -1) + 1;
    if (position > 365)
        throw fail("section", "the plan has too many sections");
    const key = work.nextKey();
    work.sections.push({
        key,
        originSectionId: null,
        position,
        kind: section.kind,
        localDate: section.localDate,
        timeZone: section.timeZone,
        title: section.title,
    });
    work.lists.set(key, []);
    work.changes.push({ op: "ADD_SECTION", lineageId: null, itemKey: null, fields: ["section"] });
}
function removeSection(work, ref) {
    const section = work.section(ref);
    if ((work.lists.get(section.key) ?? []).length > 0) {
        throw fail("section", "must be empty to be removed");
    }
    work.sections = work.sections.filter((other) => other.key !== section.key);
    work.lists.delete(section.key);
    work.affected.delete(section.key);
    work.changes.push({ op: "REMOVE_SECTION", lineageId: null, itemKey: null, fields: ["section"] });
}
function checkIndex(index, length, field) {
    if (index === undefined)
        return length;
    if (!Number.isInteger(index) || index < 0 || index > length)
        throw fail(field, "is out of range");
    return index;
}
function addItem(work, operation, context) {
    const section = work.section(operation.section);
    if (work.itemCount >= MAX_ITEMS_PER_ITINERARY)
        throw fail("item", "the plan has too many items");
    const data = buildItem(operation.item, context);
    const list = work.lists.get(section.key);
    const index = checkIndex(operation.position, list.length, "position");
    const key = work.nextKey();
    list.splice(index, 0, {
        key,
        lineageId: null,
        originItemId: null,
        position: index,
        data,
        provenance: context.provenance,
    });
    work.affected.add(section.key);
    work.changes.push({ op: "ADD_ITEM", lineageId: null, itemKey: key, fields: ["item"] });
}
function editItem(work, operation, context) {
    const { item } = work.find(operation.stableId);
    const next = applyChanges(item.data, operation.changes, context);
    work.changes.push({
        op: "EDIT_ITEM",
        lineageId: operation.stableId,
        itemKey: item.key,
        fields: changedFieldNames(item.data, next),
    });
    item.data = next;
    work.touched.add(operation.stableId);
}
function moveItem(work, operation, context) {
    if (operation.toSection === undefined &&
        operation.toPosition === undefined &&
        operation.start === undefined) {
        throw fail("MOVE_ITEM", "must move the item to a section, a position or a start");
    }
    const { sectionKey, index, item } = work.find(operation.stableId);
    const from = work.sectionOfKey(sectionKey);
    const target = operation.toSection === undefined ? from : work.section(operation.toSection);
    const sameSection = target.key === from.key;
    if (!sameSection &&
        operation.start === undefined &&
        item.data.timingKind === "SCHEDULED" &&
        target.kind === "DAY" &&
        target.localDate !== from.localDate) {
        throw fail("start", "is required when a scheduled item moves to another day");
    }
    const fields = [];
    if (operation.toSection !== undefined)
        fields.push("section");
    if (operation.toPosition !== undefined)
        fields.push("position");
    if (operation.start !== undefined) {
        item.data = retime(item.data, operation.start, context);
        fields.push("timing");
    }
    const source = work.lists.get(from.key);
    source.splice(index, 1);
    const destination = work.lists.get(target.key);
    const where = checkIndex(operation.toPosition ?? (sameSection ? index : undefined), destination.length, "toPosition");
    destination.splice(where, 0, item);
    work.affected.add(from.key);
    work.affected.add(target.key);
    work.touched.add(operation.stableId);
    work.changes.push({ op: "MOVE_ITEM", lineageId: operation.stableId, itemKey: item.key, fields });
}
function removeItem(work, operation) {
    const { sectionKey, index, item } = work.find(operation.stableId);
    work.lists.get(sectionKey).splice(index, 1);
    work.affected.add(sectionKey);
    work.touched.add(operation.stableId);
    work.removed.add(operation.stableId);
    work.changes.push({
        op: "REMOVE_ITEM",
        lineageId: operation.stableId,
        itemKey: item.key,
        fields: [],
    });
}
function finish(work, operations) {
    if (work.itemCount > MAX_ITEMS_PER_ITINERARY)
        throw fail("item", "the plan has too many items");
    const renumbered = [];
    for (const key of work.affected) {
        const list = work.lists.get(key);
        if (list === undefined)
            continue;
        list.forEach((item, index) => {
            item.position = index;
        });
        renumbered.push(key);
    }
    const sections = [...work.sections].sort(bySectionPosition);
    const items = [];
    for (const section of sections) {
        for (const item of work.lists.get(section.key) ?? []) {
            items.push({
                key: item.key,
                lineageId: item.lineageId,
                originItemId: item.originItemId,
                sectionKey: section.key,
                position: item.position,
                data: item.data,
                provenance: item.provenance,
            });
        }
    }
    return Object.freeze({
        sections: sections.map((section) => ({ ...section })),
        items,
        changes: work.changes,
        operations,
        touchedLineageIds: [...work.touched],
        removedLineageIds: [...work.removed],
        renumberedSectionKeys: renumbered,
    });
}
/**
 * Plans a list of operations against a base plan, in order, all or nothing (an operation that fails throws and nothing is planned).
 * An EDIT_ITEM changes only the fields it names: every other field of the item and every other item is carried over untouched.
 * A MOVE_ITEM renumbers (densely, in their existing order) only the sections it touches. A REMOVE_SECTION needs an empty section.
 * No operation changes `kind`, the lineage, the request link or the catalog link of an item (a different catalog item is a
 * REMOVE plus an ADD). The base is a plan of record or a draft; nothing in it is changed.
 */
export function planItineraryEdit(input) {
    const { operations, context } = input;
    if (operations.length === 0)
        throw fail("operations", "must name at least one operation");
    if (operations.length > MAX_OPERATIONS_PER_EDIT)
        throw fail("operations", "has too many operations");
    const work = new Working(input.base);
    for (const operation of operations) {
        switch (operation.op) {
            case "ADD_SECTION":
                addSection(work, operation.section);
                break;
            case "REMOVE_SECTION":
                removeSection(work, operation.section);
                break;
            case "ADD_ITEM":
                addItem(work, operation, context);
                break;
            case "EDIT_ITEM":
                editItem(work, operation, context);
                break;
            case "MOVE_ITEM":
                moveItem(work, operation, context);
                break;
            case "REMOVE_ITEM":
                removeItem(work, operation);
                break;
        }
    }
    return finish(work, operations.map((operation) => ({
        op: operation.op,
        ...("stableId" in operation ? { stableId: operation.stableId } : {}),
    })));
}
/** The kind, the catalog link and the request link of an existing item never change; a matched item that differs is refused. */
function assertSameIdentity(base, next) {
    if (base.kind !== next.kind)
        throw fail("kind", "does not change: remove the item and add another");
    if (base.catalogItemId !== next.catalogItemId) {
        throw fail("catalogItemId", "does not change: remove the item and add another");
    }
    if (base.conciergeRequestId !== next.conciergeRequestId) {
        throw fail("conciergeRequestId", "does not change");
    }
}
const sectionIdentity = (section) => (section.kind === "DAY" ? `D:${section.localDate}` : "F");
/**
 * Plans the whole content of a plan from scratch (a new plan, or a replace-plan against an ACTIVE base). Items named by `stableId`
 * keep their lineage; the others are new. An omitted staff-only field or `isFixed` of a matched item keeps the base value, so a
 * customer who replaces a plan never erases a staff note or a coordinate. Base items left out are removed. The derived operations
 * say what changed, for classification and audit.
 */
export function planItineraryReplacement(input) {
    const { base, context } = input;
    if (input.sections.length > MAX_SECTIONS_PER_ITINERARY) {
        throw fail("sections", "has too many sections");
    }
    const baseItems = new Map();
    const baseSectionOf = new Map();
    if (base !== null) {
        const sectionById = new Map(base.sections.map((section) => [section.id, section]));
        for (const item of base.items) {
            baseItems.set(item.lineageId ?? item.id, item);
            const section = sectionById.get(item.sectionId);
            if (section !== undefined)
                baseSectionOf.set(item.lineageId ?? item.id, sectionIdentity(section));
        }
    }
    const work = new Working(null);
    const operations = [];
    const used = new Set();
    const baseSectionIdentities = new Set((base?.sections ?? []).map(sectionIdentity));
    const seenSections = new Set();
    let total = 0;
    for (const entry of input.sections) {
        const section = validateItinerarySectionInput(entry.section);
        const identity = sectionIdentity(section);
        if (seenSections.has(identity))
            throw fail("sections", "has a day or an undated group twice");
        seenSections.add(identity);
        const key = work.nextKey();
        work.sections.push({
            key,
            originSectionId: null,
            position: work.sections.length,
            kind: section.kind,
            localDate: section.localDate,
            timeZone: section.timeZone,
            title: section.title,
        });
        const list = [];
        work.lists.set(key, list);
        if (!baseSectionIdentities.has(identity))
            operations.push({ op: "ADD_SECTION" });
        for (const replacement of entry.items) {
            total += 1;
            if (total > MAX_ITEMS_PER_ITINERARY)
                throw fail("item", "the plan has too many items");
            const itemKey = work.nextKey();
            const stableId = replacement.stableId;
            if (stableId === undefined) {
                list.push({
                    key: itemKey,
                    lineageId: null,
                    originItemId: null,
                    position: list.length,
                    data: buildItem(replacement.item, context),
                    provenance: context.provenance,
                });
                operations.push({ op: "ADD_ITEM" });
                work.changes.push({ op: "ADD_ITEM", lineageId: null, itemKey, fields: ["item"] });
                continue;
            }
            const original = baseItems.get(stableId);
            if (original === undefined || used.has(stableId))
                throw violation("itinerary.item_not_found");
            used.add(stableId);
            const kept = original.data;
            // Only what the actor sent is judged. A field the actor may not write keeps the base value.
            assertActorMayWrite(context, replacement.item);
            const confirmed = buildItemUnchecked({
                ...replacement.item,
                staffNote: replacement.item.staffNote === undefined ? kept.staffNote : replacement.item.staffNote,
                latitude: replacement.item.latitude === undefined ? kept.latitude : replacement.item.latitude,
                longitude: replacement.item.longitude === undefined ? kept.longitude : replacement.item.longitude,
                isFixed: replacement.item.isFixed ?? kept.isFixed,
                weatherSensitivity: replacement.item.weatherSensitivity ?? kept.weatherSensitivity,
            }, context);
            assertSameIdentity(kept, confirmed);
            // An item whose wall time did not change keeps its stored instant and entered text byte for byte.
            const sameWall = kept.timingKind === confirmed.timingKind &&
                kept.enteredLocalStart === confirmed.enteredLocalStart &&
                kept.enteredLocalEnd === confirmed.enteredLocalEnd &&
                kept.timeZone === confirmed.timeZone &&
                kept.endTimeZone === confirmed.endTimeZone &&
                kept.durationMinutes === confirmed.durationMinutes &&
                kept.enteredUtcOffsetMinutes === confirmed.enteredUtcOffsetMinutes &&
                kept.enteredEndUtcOffsetMinutes === confirmed.enteredEndUtcOffsetMinutes;
            const finalData = sameWall
                ? {
                    ...confirmed,
                    startsAt: kept.startsAt,
                    endsAt: kept.endsAt,
                    timeZoneSource: kept.timeZoneSource,
                    timeZoneObservedAt: kept.timeZoneObservedAt,
                }
                : confirmed;
            const fields = changedFieldNames(kept, finalData);
            if (fields.length > 0)
                operations.push({ op: "EDIT_ITEM", stableId });
            if (baseSectionOf.get(stableId) !== identity)
                operations.push({ op: "MOVE_ITEM", stableId });
            if (fields.length > 0 || baseSectionOf.get(stableId) !== identity) {
                work.changes.push({
                    op: baseSectionOf.get(stableId) !== identity ? "MOVE_ITEM" : "EDIT_ITEM",
                    lineageId: stableId,
                    itemKey,
                    fields,
                });
                work.touched.add(stableId);
            }
            list.push({
                key: itemKey,
                lineageId: stableId,
                originItemId: original.id,
                position: list.length,
                data: finalData,
                provenance: "REVISION_COPY",
            });
        }
    }
    for (const [lineageId] of baseItems) {
        if (used.has(lineageId))
            continue;
        work.removed.add(lineageId);
        work.touched.add(lineageId);
        operations.push({ op: "REMOVE_ITEM", stableId: lineageId });
        work.changes.push({ op: "REMOVE_ITEM", lineageId, itemKey: null, fields: [] });
    }
    for (const identity of baseSectionIdentities) {
        if (!seenSections.has(identity))
            operations.push({ op: "REMOVE_SECTION" });
    }
    return finish(work, operations);
}
/**
 * Pure. An ADD touches nothing that exists. An EDIT, a MOVE or a REMOVE names an item, and the item's facts decide. An item with no
 * facts is plain (it is not fixed and nothing links it). Request and payment references are facts about the item, not about the
 * itinerary: they never change a status.
 */
export function classifyItineraryChange(operations, itemFacts) {
    const reasons = [];
    const linkedTouched = new Set();
    const blockedRemovals = new Set();
    for (const operation of operations) {
        if (operation.stableId === undefined)
            continue;
        if (operation.op === "ADD_ITEM" || operation.op === "ADD_SECTION")
            continue;
        const facts = itemFacts.get(operation.stableId);
        if (facts === undefined)
            continue;
        const id = operation.stableId;
        const open = facts.linkedRequests.some((request) => !request.terminal);
        if (facts.isFixed)
            reasons.push({ lineageId: id, reason: "FIXED" });
        if (facts.carriesRequest)
            reasons.push({ lineageId: id, reason: "REQUEST_ITEM" });
        if (open)
            reasons.push({ lineageId: id, reason: "OPEN_REQUEST" });
        if (facts.hasLinkedPayment)
            reasons.push({ lineageId: id, reason: "LINKED_PAYMENT" });
        if (facts.carriesRequest || facts.linkedRequests.length > 0 || facts.hasLinkedPayment) {
            linkedTouched.add(id);
        }
        if (operation.op === "REMOVE_ITEM" && open)
            blockedRemovals.add(id);
    }
    return Object.freeze({
        consequential: reasons.length > 0,
        reasons,
        linkedTouched: [...linkedTouched],
        blockedRemovals: [...blockedRemovals],
    });
}
/** `LINKED_ITEM_CHANGED` (a WARNING) for each touched item that a request or payment names. */
export const linkedItemChangedIssues = (classification) => classification.linkedTouched.map((lineageId) => ({
    code: "LINKED_ITEM_CHANGED",
    severity: "WARNING",
    lineageId,
}));
// --- Views of a plan ------------------------------------------------------------------------------------------------------
/** The structural validator's input for a planned plan. Keys stand in for row ids, which the new rows do not have yet. */
export function validationInputOf(plan, trip) {
    return {
        trip,
        sections: plan.sections.map((section) => ({
            id: section.key,
            position: section.position,
            kind: section.kind,
            localDate: section.localDate,
            timeZone: section.timeZone,
        })),
        items: plan.items.map((item) => ({
            id: item.key,
            sectionId: item.sectionKey,
            position: item.position,
            kind: item.data.kind,
            status: item.data.itemStatus,
            timingKind: item.data.timingKind,
            startsAt: item.data.startsAt,
            endsAt: item.data.endsAt,
            timeZone: item.data.timeZone,
            durationMinutes: item.data.durationMinutes,
            locationName: item.data.locationName,
            latitude: item.data.latitude,
            longitude: item.data.longitude,
            enteredLocalStart: item.data.enteredLocalStart,
            enteredUtcOffsetMinutes: item.data.enteredUtcOffsetMinutes,
            endTimeZone: item.data.endTimeZone,
            enteredLocalEnd: item.data.enteredLocalEnd,
            enteredEndUtcOffsetMinutes: item.data.enteredEndUtcOffsetMinutes,
            locality: item.data.locality,
            countryCode: item.data.countryCode,
        })),
    };
}
/** The issue of a structural validation, keyed by the planned item: the lineage of an existing item, else its key. */
export function lineageIssuesOf(issues, plan) {
    const lineage = new Map(plan.items.map((item) => [item.key, item.lineageId]));
    return issues.map((issue) => ({
        code: issue.code,
        severity: issue.severity,
        lineageId: issue.itemId === null ? null : (lineage.get(issue.itemId) ?? null),
    }));
}
const iso = (value) => (value === null ? null : value.toISOString());
/**
 * The content of a plan as plain JSON, in a stable order, with nothing that differs between two plannings of the same input: no new
 * row id, no key. The application hashes it, so the evidence of phase A names the content phase B writes.
 */
export function plannedContentFingerprintInput(plan) {
    const sectionIndex = new Map(plan.sections.map((section, index) => [section.key, index]));
    return {
        sections: plan.sections.map((section) => [
            section.position,
            section.kind,
            section.localDate,
            section.timeZone,
            section.title,
        ]),
        items: plan.items.map((item) => [
            sectionIndex.get(item.sectionKey),
            item.position,
            item.lineageId,
            item.data.kind,
            item.data.itemStatus,
            item.data.title,
            item.data.timingKind,
            iso(item.data.startsAt),
            iso(item.data.endsAt),
            item.data.timeZone,
            item.data.endTimeZone,
            item.data.durationMinutes,
            item.data.enteredLocalStart,
            item.data.enteredUtcOffsetMinutes,
            item.data.enteredLocalEnd,
            item.data.enteredEndUtcOffsetMinutes,
            item.data.locationName,
            item.data.locality,
            item.data.countryCode,
            item.data.latitude,
            item.data.longitude,
            item.data.catalogItemId,
            item.data.externalPlace === null
                ? null
                : [
                    item.data.externalPlace.provider,
                    item.data.externalPlace.id,
                    item.data.externalPlace.name,
                ],
            item.data.conciergeRequestId,
            item.data.customerNote,
            item.data.staffNote,
            item.data.isFixed,
            item.data.weatherSensitivity,
        ]),
    };
}
